import { Box, Stack } from "@mui/material";
import BrandsSearchItemsCard from "./BrandsSearchItemsCard";
import { useSelector } from "react-redux";
import React from "react";
import {
  formatCurrency,
  formatDate,
  formatNumber,
  formatPercent,
} from "../../../../helpers/roundFunctions";

type StatusColor = "success" | "error";
type Props = {
  searchValue: string | null;
  sortConfig: {
    field: string | null;
    direction: "asc" | "desc";
  };
};
const BrandsSearchItems = ({ searchValue, sortConfig }: Props) => {
  const { brandPerformanceData, brandPerformanceLastRunData } = useSelector(
    (state: any) => state.businessLeaderDashBoard,
  );

  const performanceData = brandPerformanceData?.query_output || [];
  const lastRunData = brandPerformanceLastRunData?.query_output || [];
  const filteredData = searchValue
    ? performanceData.filter((item: any) =>
        item.brand_display_name
          ?.toLowerCase()
          .includes(searchValue.toLowerCase()),
      )
    : performanceData;

  const sortedData = React.useMemo(() => {
    if (!sortConfig.field) return filteredData;
    const field = sortConfig.field;
    return [...filteredData].sort((a: any, b: any) => {
      const aVal = a[field];
      const bVal = b[field];
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "string") {
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [filteredData, sortConfig]);

  const lastRunMap = new Map(
    lastRunData.map((item: any) => [item.brand_id, item]),
  );

  const getPositiveMetricColor = (value: number | null): StatusColor => {
    if (value === null || value === undefined) return "error";
    if (value > 0.25) return "success";
    return "error";
  };

  const getRoiColor = (value: number | null): StatusColor => {
    if (value === null || value === undefined) return "error";
    if (value > 0.9) return "success";
    return "error";
  };

  return (
    <>
      <Box>
        {sortedData.length === 0 ? (
          <Box
            sx={{
              height: "250px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            No Data to Display
          </Box>
        ) : (
          <Stack spacing={2}>
            {sortedData.map((item: any) => {
              const lastRun: any = lastRunMap.get(item.brand_id);
              const runDate = lastRun ? formatDate(lastRun.created) : "-";
              const runRange = lastRun
                ? `${formatDate(lastRun.from_date)} - ${formatDate(
                    lastRun.to_date,
                  )}`
                : "-";
              const runBy = lastRun?.run_by || "-";
              const status =
                lastRun?.status === "SUCCESS" ? "Success" : "Failure";
              return (
                <BrandsSearchItemsCard
                  key={item.brand_id}
                  title={item.brand_display_name}
                  brandId={String(item.brand_id)}
                  value={formatNumber(item.reg)}
                  clickOut={formatNumber(item.clicks)}
                  roi={formatPercent(item.roi)}
                  roiColor={getRoiColor(item.roi)}
                  ftd={formatNumber(item.ftd)}
                  ftdPercentage={formatPercent(item.ftd_perc)}
                  ftdColor={getPositiveMetricColor(item.ftd_perc)}
                  qftd={formatNumber(item.qftd)}
                  qftdPercentage={formatPercent(item.qftd_perc)}
                  qftdColor={getPositiveMetricColor(item.qftd_perc)}
                  cpaIncome={formatCurrency(item.cpa_income_usd)}
                  rsIncome={formatCurrency(item.revshare_income_usd)}
                  totalIncome={formatCurrency(item.total_income_usd)}
                  pv={formatNumber(item.pv)}
                  epc={formatNumber(item.epc)}
                  epcCpc={formatNumber(item.epc_cpa)}
                  runDate={runDate}
                  runRange={runRange}
                  runBy={runBy}
                  status={status}
                />
              );
            })}
          </Stack>
        )}
      </Box>
    </>
  );
};

export default BrandsSearchItems;
